import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CTA_OUTLINE, CtaStyles, ctaOverLight } from './_cta';

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  original_url?: string | null;
  source_page_urls?: string | null;
}

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'kitchen', 'bedroom', 'bathroom',
  'living room', 'closet', 'unit', 'home', 'suite',
];

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean).join(' ').toLowerCase();
}

function findInteriorPhoto(gallery: GalleryPhoto[]): GalleryPhoto | null {
  let best: GalleryPhoto | null = null;
  let bestScore = -1;
  for (const p of gallery) {
    if (!p.image) continue;
    const text = photoText(p);
    const score = INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
    if (score > bestScore) { best = p; bestScore = score; }
  }
  return best || gallery.find(p => p.image) || null;
}

const howItWorksImageDef: SlotGroupDefinition = {
  label: 'How It Works Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'VIEW FLOOR PLANS' },
  },
};

// Pre-Approved icon
function IconPreApproved() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <rect x="6" y="8" width="36" height="32" rx="2" />
      <path d="M6 16h36" />
      <path d="M16 24l4 4 8-8" />
    </svg>
  );
}

function IconFirstChoice() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <rect x="8" y="4" width="32" height="40" rx="2" />
      <path d="M16 14h16" />
      <path d="M16 22h12" />
      <path d="M16 30h8" />
      <circle cx="32" cy="36" r="3" fill="currentColor" />
    </svg>
  );
}

function IconPriority() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <path d="M24 6v36" />
      <path d="M14 12l10-6 10 6" />
      <path d="M14 20h20" />
      <path d="M18 28h12" />
      <path d="M20 36h8" />
    </svg>
  );
}

function IconCommitmentFree() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <circle cx="24" cy="20" r="12" />
      <path d="M18 20l4 4 8-8" />
      <path d="M16 36c0-4 4-8 8-8s8 4 8 8" />
    </svg>
  );
}

function IconGetToKnow() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <rect x="6" y="8" width="36" height="28" rx="2" />
      <path d="M6 16h36" />
      <path d="M14 24h6" />
      <path d="M14 30h10" />
      <path d="M28 24h6" />
      <path d="M28 30h6" />
      <line x1="24" y1="16" x2="24" y2="36" />
      <path d="M16 40h16" />
    </svg>
  );
}

function IconJoiningEasy() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10">
      <circle cx="24" cy="16" r="8" />
      <path d="M12 40c0-6.627 5.373-12 12-12s12 5.373 12 12" />
      <path d="M30 14l2 2-2 2" />
    </svg>
  );
}

export function FutureResidentsClub() {
  const title = useContentToken('futureresidentsclub.title');
  const subtitle = useContentToken('futureresidentsclub.subtitle');
  const howTitle = useContentToken('futureresidentsclub.how_title');

  const b1Title = useContentToken('futureresidentsclub.benefit1_title');
  const b1Desc = useContentToken('futureresidentsclub.benefit1_desc');
  const b2Title = useContentToken('futureresidentsclub.benefit2_title');
  const b2Desc = useContentToken('futureresidentsclub.benefit2_desc');
  const b3Title = useContentToken('futureresidentsclub.benefit3_title');
  const b3Desc = useContentToken('futureresidentsclub.benefit3_desc');
  const b4Title = useContentToken('futureresidentsclub.benefit4_title');
  const b4Desc = useContentToken('futureresidentsclub.benefit4_desc');
  const b5Title = useContentToken('futureresidentsclub.benefit5_title');
  const b5Desc = useContentToken('futureresidentsclub.benefit5_desc');
  const b6Title = useContentToken('futureresidentsclub.benefit6_title');
  const b6Desc = useContentToken('futureresidentsclub.benefit6_desc');

  const howImage = useSlotGroup('futureresidentsclub.how_image', howItWorksImageDef);
  const ctaLink = useSlotGroup('futureresidentsclub.cta_link', ctaLinkDef);

  const gallery = useGallery() as GalleryPhoto[];

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();
  const howRef = useScrollReveal<HTMLDivElement>();

  const smartPhoto = useMemo(() => findInteriorPhoto(gallery), [gallery]);
  const imageSrc = howImage.image || smartPhoto?.image;
  const imageAlt = howImage.alt || smartPhoto?.alt_text || 'Apartment interior';

  return (
    <section className="frc-section">
      <CtaStyles />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Join Our Community */}
        <div ref={headingRef} className="reveal-fade-up text-center mb-14">
          <EditableToken path="futureresidentsclub.title" defaultValue="Join Our Community" as="h2" className="font-heading text-3xl sm:text-4xl font-normal text-[var(--vibe-color-text)] mb-5 tracking-wide">
            {title}
          </EditableToken>
          <EditableToken path="futureresidentsclub.subtitle" defaultValue="When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:" as="p" className="font-body text-base text-[var(--vibe-color-text-muted)] max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </EditableToken>
        </div>

        {/* Benefits grid */}
        <div ref={gridRef} className="reveal-fade-up frc-grid">
          {/* Pre-Approved */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconPreApproved /></div>
            <EditableToken path="futureresidentsclub.benefit1_title" defaultValue="Pre-Approved" as="h3" className="frc-card-title">{b1Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit1_desc" defaultValue="Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee)." as="p" className="frc-card-desc">{b1Desc}</EditableToken>
          </div>

          {/* First Choice */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconFirstChoice /></div>
            <EditableToken path="futureresidentsclub.benefit2_title" defaultValue="First Choice" as="h3" className="frc-card-title">{b2Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit2_desc" defaultValue="As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market." as="p" className="frc-card-desc">{b2Desc}</EditableToken>
          </div>

          {/* Priority */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconPriority /></div>
            <EditableToken path="futureresidentsclub.benefit3_title" defaultValue="Priority" as="h3" className="frc-card-title">{b3Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit3_desc" defaultValue="Receive first choice and flexibility of the time-frame that you want on move-in day." as="p" className="frc-card-desc">{b3Desc}</EditableToken>
          </div>

          {/* Commitment Free */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconCommitmentFree /></div>
            <EditableToken path="futureresidentsclub.benefit4_title" defaultValue="Commitment Free" as="h3" className="frc-card-title">{b4Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit4_desc" defaultValue="Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities." as="p" className="frc-card-desc">{b4Desc}</EditableToken>
          </div>

          {/* Get to Know Us */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconGetToKnow /></div>
            <EditableToken path="futureresidentsclub.benefit5_title" defaultValue="Get to Know Us" as="h3" className="frc-card-title">{b5Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit5_desc" defaultValue="You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in." as="p" className="frc-card-desc">{b5Desc}</EditableToken>
          </div>

          {/* Joining Is Easy */}
          <div className="frc-card">
            <div className="frc-card-icon"><IconJoiningEasy /></div>
            <EditableToken path="futureresidentsclub.benefit6_title" defaultValue="Joining Is Easy" as="h3" className="frc-card-title">{b6Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit6_desc" defaultValue="Simply fill out an online application by selecting your desired floor plan, select a &quot;wait list&quot; unit, pay a holding deposit, and we'll hold your spot in line. Easy!" as="p" className="frc-card-desc">{b6Desc}</EditableToken>
          </div>
        </div>

        {/* How It Works */}
        <div ref={howRef} className="reveal-fade-up frc-how">
          <div className="frc-how-text">
            <EditableToken path="futureresidentsclub.how_title" defaultValue="How It Works" as="h3" className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text)] mb-5">
              {howTitle}
            </EditableToken>
            <RichTextContent
              path="futureresidentsclub.how_body"
              label="How It Works"
              defaultValue="<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>"
              as="div"
              className="font-body text-sm text-[var(--vibe-color-text)] leading-relaxed [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-2 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline"
            />
            <EditableSlotGroup groupPath="futureresidentsclub.cta_link" definition={ctaLinkDef}>
              <a href={ctaLink.url || '/floor-plans'} className={`mt-2 ${CTA_OUTLINE}`} style={ctaOverLight}>
                {ctaLink.label || 'VIEW FLOOR PLANS'}
              </a>
            </EditableSlotGroup>
          </div>

          <EditableSlotGroup groupPath="futureresidentsclub.how_image" definition={howItWorksImageDef}>
            <div className="frc-how-photo">
              {imageSrc ? (
                <VibesitesImage url={imageSrc} alt={imageAlt} className="w-full h-full object-cover" sizes="(min-width: 768px) 50vw, 100vw" />
              ) : (
                <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .frc-section {
          padding: 4rem 0;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .frc-section { padding: 5rem 0; }
        }

        .frc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2.5rem 2rem;
          margin-bottom: 4rem;
        }
        @media (max-width: 768px) {
          .frc-grid { grid-template-columns: 1fr; gap: 2rem; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .frc-grid { grid-template-columns: repeat(2, 1fr); }
        }

        .frc-card {
          text-align: center;
          padding: 1rem 0.5rem;
        }
        .frc-card-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem;
          color: var(--vibe-color-text);
        }
        .frc-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          margin: 0 0 0.6rem;
        }
        .frc-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-muted);
          line-height: 1.65;
          margin: 0;
        }

        .frc-how {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: start;
          padding-top: 2rem;
          border-top: 1px solid var(--vibe-color-border);
        }
        @media (max-width: 768px) {
          .frc-how { grid-template-columns: 1fr; }
        }

        .frc-how-text {
          padding-top: 0.5rem;
        }

        .frc-how-photo {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .frc-how-photo img {
          transition: transform 0.5s ease;
        }
        .frc-how-photo:hover img {
          transform: scale(1.03);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FutureResidentsClub',
  displayName: 'Future Residents Club',
  tokens: {
    'futureresidentsclub.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Join Our Community',
    },
    'futureresidentsclub.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: "When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:",
    },
    'futureresidentsclub.how_title': {
      label: 'How It Works Title',
      type: 'text',
      defaultValue: 'How It Works',
    },
    'futureresidentsclub.how_body': {
      label: 'How It Works Body',
      type: 'richtext',
      defaultValue: "<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>",
    },
    'futureresidentsclub.benefit1_title': { label: 'Pre-Approved Title', type: 'text', defaultValue: 'Pre-Approved' },
    'futureresidentsclub.benefit1_desc': { label: 'Pre-Approved Description', type: 'text', defaultValue: 'Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee).' },
    'futureresidentsclub.benefit2_title': { label: 'First Choice Title', type: 'text', defaultValue: 'First Choice' },
    'futureresidentsclub.benefit2_desc': { label: 'First Choice Description', type: 'text', defaultValue: 'As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market.' },
    'futureresidentsclub.benefit3_title': { label: 'Priority Title', type: 'text', defaultValue: 'Priority' },
    'futureresidentsclub.benefit3_desc': { label: 'Priority Description', type: 'text', defaultValue: 'Receive first choice and flexibility of the time-frame that you want on move-in day.' },
    'futureresidentsclub.benefit4_title': { label: 'Commitment Free Title', type: 'text', defaultValue: 'Commitment Free' },
    'futureresidentsclub.benefit4_desc': { label: 'Commitment Free Description', type: 'text', defaultValue: "Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities." },
    'futureresidentsclub.benefit5_title': { label: 'Get to Know Us Title', type: 'text', defaultValue: 'Get to Know Us' },
    'futureresidentsclub.benefit5_desc': { label: 'Get to Know Us Description', type: 'text', defaultValue: 'You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in.' },
    'futureresidentsclub.benefit6_title': { label: 'Joining Is Easy Title', type: 'text', defaultValue: 'Joining Is Easy' },
    'futureresidentsclub.benefit6_desc': { label: 'Joining Is Easy Description', type: 'text', defaultValue: 'Simply fill out an online application by selecting your desired floor plan, select a "wait list" unit, pay a holding deposit, and we\'ll hold your spot in line. Easy!' },
  },
  slotGroups: {
    'futureresidentsclub.how_image': howItWorksImageDef,
    'futureresidentsclub.cta_link': ctaLinkDef,
  },
};

(FutureResidentsClub as any).contentSchema = contentSchema;

export default FutureResidentsClub;
